import React, { useEffect, useState } from 'react';
import { projects } from '../data/projects';
import { SectionHeading } from './ui/SectionHeading';
import { Card } from './ui/Card';
import { Tag } from './ui/Tag';
import { Button } from './ui/Button';
import { Marquee } from './ui/Marquee';
import { Reveal } from '../lib/motion';
import { Icon } from '../lib/Icon';

function ProjectModal({ project, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);

  const hasLinks = project.hasVideo || project.hasRepo;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, background: 'var(--black-a80)', backdropFilter: 'var(--blur-sm)', WebkitBackdropFilter: 'var(--blur-sm)',
      animation: 'ba-fade-in .25s var(--ease-out) both' }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        position: 'relative', width: 'min(620px, 100%)', maxHeight: '88vh', overflowY: 'auto', padding: 'clamp(28px,4vw,44px)',
        borderRadius: 'var(--radius-xl)', background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
        boxShadow: 'var(--shadow-xl), var(--glow-sm)', animation: 'ba-scale-in .35s var(--ease-spring) both' }}>
        <div aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 120, background: 'radial-gradient(100% 100% at 50% 0%, var(--yellow-a08), transparent)', pointerEvents: 'none' }} />
        <button onClick={onClose} aria-label="Fechar" data-cursor style={{
          position: 'absolute', top: 18, right: 18, width: 38, height: 38, borderRadius: '50%',
          background: 'var(--white-a06)', border: '1px solid var(--border-default)', color: 'var(--text-secondary)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'var(--t-hover)' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent)'; e.currentTarget.style.color = '#000'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--white-a06)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
          <Icon name="x" size={18} />
        </button>
        <div style={{ position: 'relative' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--accent)' }}>{project.year}</span>
          <h3 style={{ fontSize: 'var(--text-h2)', margin: '8px 0 16px' }}>{project.title}</h3>
          <p style={{ color: 'var(--text-secondary)', fontSize: 16, lineHeight: 1.7, margin: '0 0 22px' }}>{project.description}</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 28 }}>
            {project.tech.map((t) => <Tag key={t} size="sm" variant="outline">{t}</Tag>)}
          </div>
          {hasLinks ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {project.hasRepo && (
                <Button variant="solid" as="a" href={project.repoUrl} target="_blank" rel="noopener noreferrer" iconRight={<Icon name="arrow-up-right" size={16} />}>
                  Ver projeto
                </Button>
              )}
              {project.hasVideo && (
                <Button variant="outline" as="a" href={project.videoUrl} target="_blank" rel="noopener noreferrer" iconLeft={<Icon name="play" size={15} />}>
                  Ver demo
                </Button>
              )}
            </div>
          ) : (
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Icon name="lock" size={14} /> Projeto privado / sob NDA
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function ProjectTile({ project, onClick, big }) {
  return (
    <Card style={{ height: '100%', cursor: 'pointer' }} onClick={onClick} data-cursor>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {project.featured && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', boxShadow: '0 0 8px var(--accent)', flexShrink: 0 }} />}
            <h3 style={{ fontSize: big ? 'var(--text-h2)' : 'var(--text-h3)', margin: 0 }}>{project.title}</h3>
          </div>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)', flexShrink: 0 }}>{project.year}</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: big ? 16 : 14.5, lineHeight: 1.6, margin: 0, flex: 1 }}>{project.description}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
          {project.tech.slice(0, big ? 6 : 4).map((t) => <Tag key={t} size="sm" variant="muted">{t}</Tag>)}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--accent)', fontFamily: 'var(--font-mono)', fontSize: 12, marginTop: 4 }}>
          Ver detalhes <Icon name="arrow-right" size={14} />
        </div>
      </div>
    </Card>
  );
}

export default function Projects() {
  const [active, setActive] = useState(null);
  const featured = projects.filter((p) => p.featured);
  const rest = projects.filter((p) => !p.featured);

  return (
    <section id="projetos" style={{ padding: 'var(--section-pad-y) clamp(20px,5vw,56px) 0' }}>
      <div style={{ maxWidth: 'var(--container-wide)', margin: '0 auto' }}>
        <Reveal><SectionHeading index="03" eyebrow="Projetos" title="Coisas que coloquei no mundo" /></Reveal>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 18, marginTop: 56 }}>
          {featured.map((p, i) => (
            <Reveal key={p.title} delay={i * 70} style={{ display: 'flex' }}>
              <ProjectTile project={p} big onClick={() => setActive(p)} />
            </Reveal>
          ))}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 18, marginTop: 18 }}>
          {rest.map((p, i) => (
            <Reveal key={p.title} delay={i * 60} style={{ display: 'flex' }}>
              <ProjectTile project={p} onClick={() => setActive(p)} />
            </Reveal>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 'clamp(56px,8vw,96px)' }}>
        <Marquee items={['LangChain', 'Flutter', 'Next.js', 'Kotlin', 'RAG', 'TypeScript', 'Swift', 'pgvector', 'Node.js']} speed={30} />
      </div>
      {active && <ProjectModal project={active} onClose={() => setActive(null)} />}
    </section>
  );
}
